import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import axios from "../api/axiosInstance";
import { usePmsTheme } from "../Components/PmsThemeContext";
import { readStoredUser } from "../utils/sessionUser";
import { getOnboardingMessages, getOnboardingStepDefinitions } from "./onboardingI18n";
import { OnboardingFlowProvider } from "./OnboardingFlowContext";
import OnboardingPanel from "./OnboardingPanel";
import LanguageSelectionStep from "./pages/LanguageSelectionStep";
import ThemeSelectionStep from "./pages/ThemeSelectionStep";
import RoomCategoriesQuestionnaire from "./pages/RoomCategoriesQuestionnaire";
import RoomsCountsStep from "./pages/RoomsCountsStep";
import RoomsGridStep from "./pages/RoomsGridStep";
import RoomRatesStep from "./pages/RoomRatesStep";
import ReservationSettingsStep from "./pages/ReservationSettingsStep";
import ExtrasDiscountsStep from "./pages/ExtrasDiscountsStep";
import BookingEngineStep from "./pages/BookingEngineStep";

function useOnboardingSetupShellStyles(isLight) {
  const shellStyle = useMemo(
    () => ({
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px",
      background: isLight
        ? "linear-gradient(180deg, #f7f1e4 0%, #efe5d5 100%)"
        : "radial-gradient(circle at top, rgba(92,191,255,0.14), transparent 32%), #07101b",
    }),
    [isLight]
  );

  const cardStyle = useMemo(
    () => ({
      width: "min(720px, 100%)",
      borderRadius: "28px",
      padding: "28px",
      border: isLight ? "1px solid rgba(74,53,17,0.12)" : "1px solid rgba(255,255,255,0.08)",
      background: isLight ? "rgba(255,255,255,0.92)" : "rgba(10,15,28,0.92)",
      boxShadow: isLight ? "0 24px 60px rgba(97,78,31,0.12)" : "0 24px 60px rgba(0,0,0,0.28)",
      color: isLight ? "#332515" : "#f3f7ff",
    }),
    [isLight]
  );

  return { shellStyle, cardStyle };
}

function OnboardingSetupLoading() {
  const { languageId, themeId } = usePmsTheme();
  const copy = getOnboardingMessages(languageId).setupGate;
  const isLight = themeId === "light";
  const { shellStyle, cardStyle } = useOnboardingSetupShellStyles(isLight);

  return (
    <div style={shellStyle}>
      <section style={cardStyle}>
        <div
          style={{
            fontSize: "0.78rem",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            color: isLight ? "#8f7447" : "#8ea0bc",
          }}
        >
          {copy.eyebrow}
        </div>
        <h1 style={{ margin: "10px 0 12px", fontSize: "2rem", lineHeight: 1.1 }}>{copy.loadingTitle}</h1>
        <p style={{ margin: 0, lineHeight: 1.7, color: isLight ? "#5e4b2f" : "#b9c3d7" }}>{copy.loadingCopy}</p>
      </section>
    </div>
  );
}

function OnboardingSetupGate({ setupState, onRetry }) {
  const navigate = useNavigate();
  const { languageId, themeId } = usePmsTheme();
  const copy = getOnboardingMessages(languageId).setupGate;
  const isLight = themeId === "light";
  const { shellStyle, cardStyle } = useOnboardingSetupShellStyles(isLight);

  const statusLabel =
    setupState?.setup?.status === "ready"
      ? copy.readyStatus
      : setupState?.setup?.status === "draft"
        ? copy.draftStatus
        : copy.missingStatus;
  const bookingEngineLabel =
    setupState?.setup?.booking_engine_mode === "pilcore"
      ? copy.bookingEnginePilcore
      : copy.bookingEngineNone;

  return (
    <div style={shellStyle}>
      <section style={cardStyle}>
        <div
          style={{
            fontSize: "0.78rem",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            color: isLight ? "#8f7447" : "#8ea0bc",
          }}
        >
          {copy.eyebrow}
        </div>
        <h1 style={{ margin: "10px 0 12px", fontSize: "2rem", lineHeight: 1.1 }}>{copy.title}</h1>
        <p style={{ margin: 0, lineHeight: 1.7, color: isLight ? "#5e4b2f" : "#b9c3d7" }}>{copy.copy}</p>

        <div
          style={{
            marginTop: "22px",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "14px",
          }}
        >
          <div
            style={{
              borderRadius: "18px",
              padding: "16px",
              border: isLight ? "1px solid rgba(74,53,17,0.1)" : "1px solid rgba(255,255,255,0.08)",
              background: isLight ? "rgba(255,255,255,0.75)" : "rgba(255,255,255,0.03)",
            }}
          >
            <div style={{ fontSize: "0.82rem", color: isLight ? "#8f7447" : "#8ea0bc" }}>{copy.statusLabel}</div>
            <div style={{ marginTop: "8px", fontWeight: 700 }}>{statusLabel}</div>
          </div>
          <div
            style={{
              borderRadius: "18px",
              padding: "16px",
              border: isLight ? "1px solid rgba(74,53,17,0.1)" : "1px solid rgba(255,255,255,0.08)",
              background: isLight ? "rgba(255,255,255,0.75)" : "rgba(255,255,255,0.03)",
            }}
          >
            <div style={{ fontSize: "0.82rem", color: isLight ? "#8f7447" : "#8ea0bc" }}>{copy.bookingEngineLabel}</div>
            <div style={{ marginTop: "8px", fontWeight: 700 }}>{bookingEngineLabel}</div>
          </div>
        </div>

        {setupState?.error ? (
          <div
            style={{
              marginTop: "18px",
              borderRadius: "16px",
              padding: "14px 16px",
              background: isLight ? "rgba(154,54,54,0.08)" : "rgba(140,34,34,0.28)",
              color: isLight ? "#7a1f1f" : "#ffd6d6",
              border: isLight ? "1px solid rgba(154,54,54,0.16)" : "1px solid rgba(255,255,255,0.08)",
            }}
          >
            {setupState.error}
          </div>
        ) : null}

        <div style={{ marginTop: "24px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={onRetry}
            style={{
              borderRadius: "14px",
              border: "none",
              padding: "12px 16px",
              background: isLight ? "#d49d47" : "#5cbfff",
              color: isLight ? "#24160a" : "#08111e",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {copy.retryAction}
          </button>
          <button
            type="button"
            onClick={() => navigate("/select-module")}
            style={{
              borderRadius: "14px",
              border: isLight ? "1px solid rgba(74,53,17,0.16)" : "1px solid rgba(255,255,255,0.12)",
              padding: "12px 16px",
              background: "transparent",
              color: isLight ? "#332515" : "#f3f7ff",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {copy.backAction}
          </button>
        </div>
      </section>
    </div>
  );
}

export default function OnboardingApp() {
  const sessionUser = readStoredUser();
  const allowedModules = Array.isArray(sessionUser?.allowed_modules) ? sessionUser.allowed_modules : [];
  const [setupState, setSetupState] = useState({
    loading: true,
    ready: false,
    setup: null,
    error: "",
  });
  const bookingEngineEnabled = setupState?.setup?.booking_engine_mode === "pilcore";
  const onboardingSteps = useMemo(
    () => getOnboardingStepDefinitions({ bookingEngineEnabled }),
    [bookingEngineEnabled]
  );

  const loadSetup = async () => {
    setSetupState((current) => ({
      ...current,
      loading: true,
      error: "",
    }));

    try {
      const response = await axios.get("/api/onboarding/setup");
      setSetupState({
        loading: false,
        ready: Boolean(response.data?.ready),
        setup: response.data?.setup || null,
        error: "",
      });
    } catch (error) {
      console.error("[ONBOARDING] Could not load setup gate:", error);
      setSetupState({
        loading: false,
        ready: false,
        setup: error?.response?.data?.setup || null,
        error: error?.response?.data?.error || "Could not load onboarding access.",
      });
    }
  };

  useEffect(() => {
    loadSetup();
  }, []);

  if (!allowedModules.includes("onboarding")) {
    return <Navigate to="/select-module" replace />;
  }

  if (setupState.loading) {
    return <OnboardingSetupLoading />;
  }

  if (!setupState.ready) {
    return <OnboardingSetupGate setupState={setupState} onRetry={loadSetup} />;
  }

  return (
    <OnboardingFlowProvider steps={onboardingSteps}>
      <Routes>
        <Route element={<OnboardingPanel />}>
          <Route path="language" element={<LanguageSelectionStep />} />
          <Route path="theme" element={<ThemeSelectionStep />} />
          <Route path="room-categories" element={<RoomCategoriesQuestionnaire />} />
          <Route path="rooms" element={<Navigate to="/onboarding/rooms-counts" replace />} />
          <Route path="rooms-counts" element={<RoomsCountsStep />} />
          <Route path="rooms-grid" element={<RoomsGridStep />} />
          <Route
            path="room-rates"
            element={<RoomRatesStep bookingEngineEnabled={setupState?.setup?.booking_engine_mode === "pilcore"} />}
          />
          <Route path="reservation-settings" element={<ReservationSettingsStep />} />
          <Route path="extras-discounts" element={<ExtrasDiscountsStep />} />
          {bookingEngineEnabled ? <Route path="booking-engine" element={<BookingEngineStep />} /> : null}
          <Route path="*" element={<Navigate to="language" replace />} />
        </Route>
      </Routes>
    </OnboardingFlowProvider>
  );
}
